import React, { MouseEvent, useState } from 'react'
import { buildControlsExt } from '../../inputs/controls'
import { useFormValidation } from '../../../hooks/useFormValidation'
import { getGeneralSettingsValidationSchema } from '../utils/getGeneralSettingsValidationSchema'
import * as yup from 'yup'
import { useEffectAfterMount } from '../../../hooks/useEffectAfterMount'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { modifyGeneralSettings } from '../../../store/actions/coreSettingsActions'
import { getGeneralSettingsControlDescriptors } from '../utils/getGeneralSettingsControlDescriptors'
import { updateProductGeneralSettings } from '../../../store/async-thunks/settings/updateProductGeneralSettings'
import { settingsActions } from '../../../store/slices/settingsSlice'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { selectProductGeneralSettingsDefault } from '../../../store/selectors/settingsSelectors'
import _isEqual from 'lodash/isEqual'

interface Props {
    guid: string
    generalSettings: any
}

export const GeneralSettingsFields = ({ guid, generalSettings }: Props) => {
    const dispatch = useAppDispatch()
    const [isApplyDisabled, setIsApplyDisabled] = useState(false)
    const generalSettingsDefault = useAppSelector(
        selectProductGeneralSettingsDefault(guid)
    )

    const validationSchema = getGeneralSettingsValidationSchema()
    const [state, setState, touched, setTouched, validationResult] =
        useFormValidation(generalSettings, validationSchema)

    const checkSchema = yup.object().shape(validationSchema)
    const isFormValid = checkSchema.isValidSync(state)
    const disableIsDuplicatePreventionEnabled =
        !generalSettingsDefault?.hasOwnProperty('is_duplicate_prevention_enabled')

    const isTouched = Object.keys(touched).length
    const isSame = _isEqual(generalSettingsDefault, state)

    const handleApply = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        setIsApplyDisabled(true)
        dispatch(updateProductGeneralSettings({ guid, generalSettings: state }))
        setTimeout(() => setIsApplyDisabled(false), 3000)
    }

    const handleReset = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        dispatch(settingsActions.resetGeneralSettings(guid))
        setState(generalSettingsDefault)
    }

    const controlDescriptors = getGeneralSettingsControlDescriptors(state, {
        disableIsDuplicatePreventionEnabled,
    })

    const dataArr = buildControlsExt(
        controlDescriptors,
        state,
        setState,
        touched,
        setTouched,
        validationResult
    )

    useEffectAfterMount(() => {
        dispatch(modifyGeneralSettings({ guid, data: state }))
    }, [state])

    return (
        <div className="d-flex flex-column gap-4 px-2 pt-4">
            <div className="d-flex flex-column gap-2">
                {!!dataArr.length &&
                    dataArr.map((el, i) => <div key={i}>{el}</div>)}
            </div>
            <div className="d-flex gap-2">
                <button
                    type="button"
                    onClick={handleReset}
                    className="app-btn-main app-btn-apply fit-content mt-2"
                    disabled={isSame}
                >
                    Reset
                </button>
                <button
                    type="button"
                    onClick={handleApply}
                    className="app-btn-main app-btn-reset fit-content mt-2"
                    disabled={isSame || isApplyDisabled || !isTouched || !isFormValid}
                >
                    Apply
                </button>
            </div>
        </div>
    )
}
